import axios from 'axios';

import { URL_CONSTANTS } from '../../../../constants/urlConstants';
import apiService from '../../../../services/api.service';
import { getAxiosExceptionMessage } from '../../../../utils/common.helper';
import { MUNICIPALITY_API_BASE } from '../constants/municipalityRegistration.constants';
import type {
  MunicipalityDocument,
  MunicipalityListResult,
  MunicipalityNote,
  MunicipalityRecord,
  PermitMeta,
  SaveMunicipalityParams,
  SelectOption,
} from '../interfaces/municipalityRegistration.interface';
import {
  apiToUi,
  areMunicipalityFormValuesEqual,
  buildComparableMunicipalityPayload,
  buildMunicipalityApiPayload,
  mapDocumentForUi,
  mapNoteForUi,
  triStateDbToList,
  unwrapApiData,
} from '../helpers/municipalityRegistration.mapper';

export type {
  MunicipalityApiPayload,
  MunicipalityDocument,
  MunicipalityListRow,
  MunicipalityListResult,
  MunicipalityNote,
  MunicipalityRecord,
  PermitMeta,
  SaveMunicipalityParams,
  SelectOption,
} from '../interfaces/municipalityRegistration.interface';

export {
  areMunicipalityFormValuesEqual,
  buildComparableMunicipalityPayload,
  buildMunicipalityApiPayload,
  triStateDbToList,
};

const BASE = MUNICIPALITY_API_BASE;

// ─── API calls ────────────────────────────────────────────────────────────────

export const getMunicipalityList = async (
  _rows?: number,
  _page?: number,
  _sortBy?: string,
  _orderBy?: number | string,
  _search?: string
): Promise<MunicipalityListResult> => {
  const limit = _rows || 10;
  const offset = (_page || 0) * limit;
  const sortOrder =
    _orderBy === 1 || _orderBy === 'ASC' || _orderBy === 'asc' ? 'ASC' : 'DESC';

  const params = new URLSearchParams({
    limit: String(limit),
    offset: String(offset),
    search: _search?.trim() || '',
  });
  if (_sortBy) {
    params.set('sortBy', _sortBy);
    params.set('sortOrder', sortOrder);
  }

  try {
    const response = await apiService.get(`${BASE}?${params.toString()}`);
    const data = unwrapApiData(response);
    return {
      records: (data?.rows || []).map((r: unknown) => {
        const ui = apiToUi(r);
        return {
          id: ui.id,
          state: (ui.state as Record<string, string>)?.state_name || '-',
          municipality: ui.municipalityName || '-',
          county: ui.county || '-',
          type: ui.typeLabel || (ui.type as SelectOption)?.label || '-',
          certNumber: ui.documentNumber || '-',
          expirationDate: ui.expiryDate || null,
          permitRequired: triStateDbToList(
            (r as Record<string, unknown>)?.permit_required ??
              (r as Record<string, unknown>)?.permitRequired ??
              ui.permitRequired
          ),
          registrationRequired: triStateDbToList(
            (r as Record<string, unknown>)?.registration_required ??
              (r as Record<string, unknown>)?.registrationRequired ??
              ui.registrationRequired
          ),
          status:
            ui.statusLabel ||
            (ui.status as Record<string, string>)?.status ||
            (ui.status as SelectOption)?.label ||
            '-',
          district: ui.district || [],
        };
      }),
      totalCount: data?.count || 0,
    };
  } catch (error) {
    throw new Error(getAxiosExceptionMessage(error, 'Failed to fetch list'));
  }
};

export const getMunicipalityById = async (
  id: number | string
): Promise<MunicipalityRecord> => {
  try {
    const response = await apiService.get(`${BASE}/${id}`);
    return apiToUi(unwrapApiData(response));
  } catch (error) {
    throw new Error(getAxiosExceptionMessage(error, 'Failed to fetch details'));
  }
};

export const createMunicipality = async (
  payload: Partial<MunicipalityRecord>
): Promise<MunicipalityRecord> => {
  try {
    const response = await apiService.post(
      BASE,
      buildMunicipalityApiPayload(payload)
    );
    return apiToUi(unwrapApiData(response));
  } catch (error) {
    throw new Error(
      getAxiosExceptionMessage(error, 'Failed to create municipality')
    );
  }
};

export const updateMunicipality = async (
  id: number | string,
  payload: Partial<MunicipalityRecord>
): Promise<MunicipalityRecord> => {
  try {
    const response = await apiService.patch(
      `${BASE}/${id}`,
      buildMunicipalityApiPayload(payload)
    );
    return apiToUi(unwrapApiData(response));
  } catch (error) {
    throw new Error(
      getAxiosExceptionMessage(error, 'Failed to update municipality')
    );
  }
};

export const getMunicipalityNotes = async (
  registrationId: number | string
): Promise<MunicipalityNote[]> => {
  try {
    const response = await apiService.get(`${BASE}/${registrationId}/notes`);
    const data = unwrapApiData(response);
    const rows = Array.isArray(data) ? data : [];
    return rows.map(mapNoteForUi);
  } catch (error) {
    throw new Error(getAxiosExceptionMessage(error, 'Failed to load notes'));
  }
};

export const createMunicipalityNote = async (
  registrationId: number | string,
  noteText: string
): Promise<MunicipalityNote> => {
  try {
    const response = await apiService.post(`${BASE}/${registrationId}/notes`, {
      note_text: noteText,
    });
    return mapNoteForUi(unwrapApiData(response));
  } catch (error) {
    throw new Error(getAxiosExceptionMessage(error, 'Failed to save note'));
  }
};

export const saveMunicipalityRegistration = async ({
  id,
  payload,
  notes = [],
  pendingDocuments = [],
  skipRecordUpdate = false,
}: SaveMunicipalityParams): Promise<Partial<MunicipalityRecord>> => {
  let saved: Partial<MunicipalityRecord>;
  if (id && skipRecordUpdate) {
    saved = { id: Number(id) };
  } else if (id) {
    saved = await updateMunicipality(id, payload);
  } else {
    saved = await createMunicipality(payload);
  }
  const registrationId = saved?.id ?? id;

  if (registrationId == null || registrationId === '') {
    throw new Error('Municipality registration was saved without an id');
  }

  const newNotes = notes
    .filter(note => !note?.id)
    .map(note => (typeof note === 'string' ? note : note?.text))
    .map(text => (text || '').trim())
    .filter(Boolean);

  for (const noteText of newNotes) {
    // eslint-disable-next-line no-await-in-loop
    await createMunicipalityNote(registrationId, noteText);
  }

  for (const file of pendingDocuments) {
    if (!file) continue;
    // eslint-disable-next-line no-await-in-loop
    await uploadMunicipalityDocument(registrationId, file);
  }

  try {
    return await getMunicipalityById(registrationId);
  } catch {
    return saved;
  }
};

export const getMunicipalityStates = async (
  search = ''
): Promise<SelectOption[]> => {
  const response = await apiService.get(
    `${BASE}/meta/states?search=${encodeURIComponent(search)}`
  );
  return unwrapApiData(response);
};

export const getMunicipalityDistricts = async (
  search = ''
): Promise<SelectOption[]> => {
  const response = await apiService.get(
    `${BASE}/meta/districts?search=${encodeURIComponent(search)}`
  );
  return unwrapApiData(response);
};

export const getMunicipalityTypes = async (
  search = ''
): Promise<SelectOption[]> => {
  const url = search
    ? `/system/types/Municipality%20Registration?search=${encodeURIComponent(search)}`
    : `/system/types/Municipality%20Registration`;
  const response = await apiService.get(url);
  const rows: { type_id: number; label: string }[] = Array.isArray(response)
    ? response
    : (response?.data ?? []);
  return rows.map(row => ({ value: row.type_id, label: row.label }));
};

export const getMunicipalityStatuses = async (
  search = ''
): Promise<SelectOption[]> => {
  const response = await apiService.get(
    `${BASE}/meta/statuses?search=${encodeURIComponent(search)}`
  );
  return unwrapApiData(response);
};

export const getMunicipalityPermitMetadata = async (): Promise<PermitMeta> => {
  const response = await apiService.get(`${BASE}/meta/permit-config`);
  return unwrapApiData(response);
};

interface ProjectCategoryRow {
  category: string;
  project_category_id: number;
  is_deleted?: number;
  parent_id?: number | null;
}

const mapProjectCategoryRows = (rows: ProjectCategoryRow[]): SelectOption[] =>
  rows
    .filter(
      row =>
        (row.is_deleted === 0 || row.is_deleted == null) &&
        (row.parent_id == null || row.parent_id === undefined)
    )
    .map(row => ({
      label: row.category,
      value: row.project_category_id,
    }))
    .sort((a, b) => String(a.label).localeCompare(String(b.label)));

/** Project categories — same source as project module (t_project_categories) */
export const getMunicipalityCategories = async (
  search = ''
): Promise<SelectOption[]> => {
  const term = encodeURIComponent(search);

  try {
    const metaResponse = await apiService.get(
      `${BASE}/meta/categories?search=${term}`
    );
    const metaRows = unwrapApiData(metaResponse);
    if (Array.isArray(metaRows) && metaRows.length) {
      return metaRows.map((row: SelectOption) => ({
        label: String(row.label),
        value: Number(row.value),
      }));
    }
  } catch {
    // fall through to project master endpoint
  }

  const params = search ? `?searchString=${term}` : '';
  const response = await apiService.get(
    `/projects/master/project-category${params}`
  );
  const rows = Array.isArray(response) ? response : unwrapApiData(response);
  if (!Array.isArray(rows)) return [];

  return mapProjectCategoryRows(rows as ProjectCategoryRow[]);
};

export const seedMunicipalityDefaults = async (): Promise<unknown> => {
  try {
    const response = await apiService.post(`${BASE}/meta/seed-defaults`, {});
    return unwrapApiData(response);
  } catch (error) {
    const status = (error as { response?: { status?: number } })?.response
      ?.status;
    if (status === 401 || status === 403 || status === 404) return null;
    throw error;
  }
};

export const getMunicipalityDocuments = async (
  registrationId: number | string
): Promise<MunicipalityDocument[]> => {
  const response = await apiService.get(`${BASE}/${registrationId}/documents`);
  const data = unwrapApiData(response);
  if (Array.isArray(data)) return data.map(mapDocumentForUi);
  if (Array.isArray(data?.rows)) return data.rows.map(mapDocumentForUi);
  return [];
};

/** Fresh S3 pre-signed URL for view/download (avoids expired URLs from list/cache). */
export const getMunicipalityDocumentById = async (
  registrationId: number | string,
  documentId: number | string
): Promise<MunicipalityDocument> => {
  try {
    const response = await apiService.get(
      `${BASE}/${registrationId}/documents/${documentId}`
    );
    return mapDocumentForUi(unwrapApiData(response));
  } catch (error) {
    throw new Error(
      getAxiosExceptionMessage(error, 'Failed to load document access URL')
    );
  }
};

export const uploadMunicipalityDocument = async (
  registrationId: number | string,
  file: File
): Promise<MunicipalityDocument | MunicipalityDocument[]> => {
  const formData = new FormData();
  formData.append('file', file);

  const headers: Record<string, string> = {
    Accept: 'application/json',
    ...((axios.defaults.headers.common as Record<string, string>) || {}),
  };

  if (!headers.Authorization) {
    const token = localStorage.getItem('access_token');
    if (token) headers.Authorization = `Bearer ${token}`;
  }
  if (!headers['client-id']) {
    const clientId = localStorage.getItem('client_id');
    if (clientId) headers['client-id'] = clientId;
  }
  if (!headers['device-type']) headers['device-type'] = 'web_app';

  try {
    const response = await axios.post(
      `${URL_CONSTANTS.API.BASE_URL}${BASE}/${registrationId}/documents`,
      formData,
      { headers }
    );
    const payload = unwrapApiData(response?.data);
    if (Array.isArray(payload)) return payload.map(mapDocumentForUi);
    return mapDocumentForUi(payload);
  } catch (error) {
    throw new Error(
      getAxiosExceptionMessage(error, 'Failed to upload document')
    );
  }
};

export const deleteMunicipalityDocument = async (
  registrationId: number | string,
  documentId: number | string
): Promise<true> => {
  await apiService.delete(`${BASE}/${registrationId}/documents/${documentId}`);
  return true;
};
